// AI Chat & Search Panel for Organizer - Slide-in from right
import { useState } from 'react';
import { X, Save } from 'lucide-react';
import AIAssistPanel, { AssistantMessage } from './AIAssistPanel';
import { supabase } from '@/lib/supabase';

interface OrganizerAIPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveToScratchPad?: (content: string, title?: string) => void;
}

export default function OrganizerAIPanel({ isOpen, onClose, onSaveToScratchPad }: OrganizerAIPanelProps) {
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [busy, setBusy] = useState(false);

  const handleSendChat = async (text: string) => {
    const userMsg: AssistantMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      variant: 'chat',
      timestamp: Date.now(),
    };
    setMessages(prev => [...prev, userMsg]);
    setBusy(true);

    try {
      // Call Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('ai-assist', {
        body: {
          mode: 'chat',
          payload: {
            conversation: [...messages, userMsg].map(m => ({
              role: m.role,
              content: m.content,
            })),
            webSearch: true, // Enable web search
          },
        },
      });

      if (error) throw error;

      const aiResponse = data?.result?.reply || 'Sorry, I could not process that request.';
      
      const aiMsg: AssistantMessage = {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content: aiResponse,
        variant: 'chat',
        timestamp: Date.now(),
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch (error) {
      console.error('AI chat error:', error);
      const errorMsg: AssistantMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, there was an error processing your request. Please try again.',
        variant: 'chat',
        timestamp: Date.now(),
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setBusy(false);
    }
  };

  const handleRunCommand = async (text: string) => {
    // Commands not needed for organizer AI
    handleSendChat(text);
  };

  const handleSaveLastResponse = () => {
    const lastAIMessage = [...messages].reverse().find(m => m.role === 'assistant');
    if (lastAIMessage && onSaveToScratchPad) {
      onSaveToScratchPad(lastAIMessage.content, 'AI Response');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-full w-full sm:w-[500px] bg-gradient-to-b from-[#0a0513] via-[#0f051a] to-[#1a0a2e] border-l-2 border-emerald-400/30 shadow-2xl z-[9999] transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-emerald-400/20 bg-gradient-to-r from-emerald-600/20 to-teal-600/20">
          <div>
            <h2 className="text-xl font-bold text-white">AI Chat & Search</h2>
            <p className="text-xs text-emerald-300">Ask anything • Search the web • Get instant answers</p>
          </div>
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <button
                onClick={handleSaveLastResponse}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                title="Save last response to Scratch Pad"
              >
                <Save className="w-5 h-5 text-emerald-300" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-emerald-300" />
            </button>
          </div>
        </div>

        {/* AI Panel Content */}
        <div className="h-[calc(100%-80px)]">
          <AIAssistPanel
            messages={messages}
            busy={busy}
            onSendChat={handleSendChat}
            onRunCommand={handleRunCommand}
            onSaveToScratchPad={onSaveToScratchPad}
          />
        </div>
      </div>
    </>
  );
}
